export type Polygon = ReadonlyArray<readonly [number, number]>;
export type Bbox = { minX: number; minY: number; maxX: number; maxY: number };
export type NormalisedPoint = { x: number; y: number };

/** Converts a polygon to an SVG path string ("M x y L x y … Z"). Empty polygon returns "". */
export function polygonToPath(polygon: Polygon): string {
  if (polygon.length === 0) return '';
  const [first, ...rest] = polygon;
  const move = `M ${first[0]} ${first[1]}`;
  const lines = rest.map(([x, y]) => `L ${x} ${y}`).join(' ');
  return lines.length > 0 ? `${move} ${lines} Z` : `${move} Z`;
}

/** Returns the axis-aligned bounding box of a polygon. Empty polygon returns all-zero bbox. */
export function polygonBbox(polygon: Polygon): Bbox {
  if (polygon.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  }
  let minX = polygon[0][0];
  let maxX = polygon[0][0];
  let minY = polygon[0][1];
  let maxY = polygon[0][1];
  for (const [x, y] of polygon) {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return { minX, minY, maxX, maxY };
}

export function parsePosition(raw: unknown): { x: number; y: number } | null {
  if (
    raw !== null &&
    typeof raw === "object" &&
    !Array.isArray(raw) &&
    typeof (raw as Record<string, unknown>).x === "number" &&
    typeof (raw as Record<string, unknown>).y === "number"
  ) {
    return raw as { x: number; y: number };
  }
  return null;
}

/** Maps a normalised 0–1 point relative to a bbox back into viewport coordinates. */
export function normalisedToViewport(
  p: NormalisedPoint,
  bbox: Bbox,
): { x: number; y: number } {
  const w = bbox.maxX - bbox.minX;
  const h = bbox.maxY - bbox.minY;
  return {
    x: bbox.minX + p.x * w,
    y: bbox.minY + p.y * h,
  };
}
